<template>
  <ModalAiConsent v-model:show="showConsentModal" @ai-terms-agreement-agreed="onConsentAgreed" />

  <div v-if="guidance" class="rate-guidance" data-test="rate-guidance-widget">
    <WdsStack direction-column gap-xs>
      <WdsStack align-center gap-xs>
        <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" :size="14" />
        <WdsText small bold>{{ $t('Rate guidance') }}</WdsText>
        <WdsText v-if="confidenceLabel" small secondary data-test="rate-guidance-confidence">
          · {{ confidenceLabel }}
        </WdsText>
      </WdsStack>

      <div class="rate-guidance__range">
        <WdsStack justify-space-between>
          <WdsText small secondary>{{ formatRate(guidance.low) }}</WdsText>
          <WdsText small bold>{{ formatRate(guidance.median) }}</WdsText>
          <WdsText small secondary>{{ formatRate(guidance.high) }}</WdsText>
        </WdsStack>

        <div class="rate-guidance__bar">
          <div class="rate-guidance__bar-track" />
          <div
            v-if="currentRatePosition !== null"
            class="rate-guidance__bar-marker"
            :style="{ left: `${currentRatePosition}%` }"
            data-test="rate-guidance-marker"
          />
        </div>

        <WdsStack justify-space-between>
          <WdsText small secondary>{{ $t('Low') }}</WdsText>
          <WdsText small secondary>{{ $t('High') }}</WdsText>
        </WdsStack>
      </div>

      <WdsText v-if="guidance.summary" small secondary class="tw-mt-1">
        {{ guidance.summary }}
      </WdsText>

      <WdsText small disabled-text class="tw-mt-1">
        {{ $t('AI-generated estimate. Actual rates may vary.') }}
      </WdsText>
    </WdsStack>
  </div>

  <div v-else-if="isLoading" class="rate-guidance rate-guidance--loading" data-test="rate-guidance-loading">
    <WdsStack align-center gap-xs>
      <img
        src="/gifs/worksome-assistant-thinking.gif"
        :alt="$t('Worksome Assistant thinking')"
        style="height: 16px"
      />
      <WdsText small secondary>{{ $t('Getting rate guidance...') }}</WdsText>
    </WdsStack>
  </div>

  <WdsButtonGhost
    v-else-if="canFetch && !hasError"
    secondary
    data-test="rate-guidance-trigger"
    @click="handleClick"
  >
    <WdsStack align-center gap-xs>
      <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" :size="14" />
      <WdsText small>{{ $t('Get rate guidance') }}</WdsText>
    </WdsStack>
  </WdsButtonGhost>

  <div v-else-if="hasError" class="rate-guidance" data-test="rate-guidance-error">
    <WdsStack align-center justify-space-between>
      <WdsText small secondary>{{ $t('Rate guidance unavailable') }}</WdsText>
      <WdsButtonGhost secondary @click="fetchGuidance">
        <WdsText small>{{ $t('Retry') }}</WdsText>
      </WdsButtonGhost>
    </WdsStack>
  </div>
</template>

<script setup lang="ts">
import {
  WdsStack,
  WdsText,
  WdsIcon,
  WdsIconNameEnum,
  WdsButtonGhost,
} from '@worksome/wds-components'
import { computed, ref, watch } from 'vue'

import api from '@/api/api-ai'
import { useTranslation } from '@/composables/translation'
import ModalAiConsent from '@/components/LLM/ModalAiConsent.vue'
import { useAiConsent } from '@/composables/useAiConsent'

interface RateGuidanceResponse {
  low: number
  median: number
  high: number
  currency: string
  rateType: string
  confidence: string
  summary: string
}

const props = withDefaults(
  defineProps<{
    roleTitle: string
    rateType: string
    currency: string
    currentRate?: number
    country?: string | null
    jobDescription?: string | null
    skills?: string[] | null
    seniorityLevel?: string | null
    jobId?: string | null
  }>(),
  {
    currentRate: 0,
    country: null,
    jobDescription: null,
    skills: null,
    seniorityLevel: null,
    jobId: null,
  },
)

const { $t } = useTranslation()
const { needsConsent, showConsentModal } = useAiConsent()

const guidance = ref<RateGuidanceResponse | null>(null)
const isLoading = ref(false)
const hasError = ref(false)

// A market rate for a fixed amount means nothing without the scope, so fixed gets no guidance.
const canFetch = computed(
  () =>
    props.roleTitle.trim().length > 0 &&
    props.rateType.length > 0 &&
    props.rateType !== 'fixed' &&
    props.currency.length > 0,
)

const currentRatePosition = computed(() => {
  if (!guidance.value || !props.currentRate || props.currentRate <= 0) {
    return null
  }

  const { low, high } = guidance.value
  const range = high - low

  if (range <= 0) return 50

  const position = ((props.currentRate - low) / range) * 100

  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, position))
})

const confidenceLabel = computed(() => {
  switch (guidance.value?.confidence) {
    case 'high':
      return $t('High confidence')
    case 'moderate':
      return $t('Moderate confidence')
    case 'low':
      return $t('Low confidence')
    default:
      return null
  }
})

const formatRate = (value: number): string => {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

  return `${props.currency} ${formatted}`
}

// Bumped whenever the inputs change, so an answer for old inputs is dropped rather than
// shown under the new rate type or currency.
let requestId = 0

const fetchGuidance = async () => {
  if (isLoading.value || !canFetch.value) return

  hasError.value = false
  requestId += 1
  const currentRequest = requestId

  try {
    isLoading.value = true

    const payload: Record<string, unknown> = {
      role_title: props.roleTitle,
      rate_type: props.rateType,
      currency: props.currency,
    }

    if (props.country) payload.country = props.country
    if (props.jobDescription) payload.job_description = props.jobDescription
    if (props.skills && props.skills.length > 0) payload.skills = props.skills
    if (props.seniorityLevel) payload.seniority_level = props.seniorityLevel
    if (props.jobId) payload.job_id = props.jobId

    const response = await api.generateRateGuidance(payload)

    if (currentRequest === requestId && response.data) {
      guidance.value = response.data as RateGuidanceResponse
    }
  } catch {
    if (currentRequest === requestId) {
      hasError.value = true
      guidance.value = null
    }
  } finally {
    if (currentRequest === requestId) {
      isLoading.value = false
    }
  }
}

const onConsentAgreed = () => {
  fetchGuidance()
}

const handleClick = () => {
  if (needsConsent.value) {
    showConsentModal.value = true
  } else {
    fetchGuidance()
  }
}

// Reset guidance, and drop any request still in flight, when any input sent to the model changes
watch(
  () => [
    props.roleTitle,
    props.rateType,
    props.currency,
    props.country,
    props.jobDescription,
    props.skills?.join('\n'),
    props.seniorityLevel,
    props.jobId,
  ],
  () => {
    requestId += 1
    isLoading.value = false
    guidance.value = null
    hasError.value = false
  },
)
</script>

<style scoped>
.rate-guidance {
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  margin-top: 8px;
}

.rate-guidance--loading {
  background: #f9fafb;
}

.rate-guidance__range {
  margin-top: 4px;
}

.rate-guidance__bar {
  position: relative;
  height: 8px;
  margin: 6px 0;
}

.rate-guidance__bar-track {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #e5e7eb 0%, #634cee 50%, #e5e7eb 100%);
}

.rate-guidance__bar-marker {
  position: absolute;
  top: -3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #634cee;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transform: translateX(-50%);
  transition: left 0.3s ease;
}
</style>
